import { html } from 'htm/preact'

export function SourcesMonitoring({ sources, onRefresh }) {
  if (!sources || sources.length === 0) return null

  const getSourceStatusColor = (source) => {
    if (source.consecutive_failures >= 3) {
      return 'bg-red-100 text-red-800 dark:bg-red-800 dark:text-red-100 border-red-200 dark:border-red-700'
    }
    if (source.consecutive_failures > 0) {
      return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-800 dark:text-yellow-100 border-yellow-200 dark:border-yellow-700'
    }
    if (source.is_executing) {
      return 'bg-blue-100 text-blue-800 dark:bg-blue-800 dark:text-blue-100 border-blue-200 dark:border-blue-700'
    }
    return 'bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100 border-green-200 dark:border-green-700'
  }

  const getSourceStatusText = (source) => {
    if (source.consecutive_failures >= 3) return 'Failed'
    if (source.consecutive_failures > 0) return 'Unstable'
    if (source.is_executing) return 'Running'
    return 'Healthy'
  }

  const getSourceStatusIcon = (source) => {
    if (source.consecutive_failures >= 3) {
      return html`
        <svg class="w-4 h-4 text-red-500" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"/>
        </svg>
      `
    }
    if (source.consecutive_failures > 0) {
      return html`
        <svg class="w-4 h-4 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/>
        </svg>
      `
    }
    if (source.is_executing) {
      return html`
        <div class="w-4 h-4">
          <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
        </div>
      `
    }
    return html`
      <svg class="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
        <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
      </svg>
    `
  }

  const getPriorityText = (priority) => {
    switch (priority) {
      case 0: return 'Low'
      case 2: return 'High'
      default: return 'Normal'
    }
  }

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 0: return 'text-gray-600 dark:text-gray-400'
      case 2: return 'text-red-600 dark:text-red-400'
      default: return 'text-blue-600 dark:text-blue-400'
    }
  }

  const formatTime = (timeString) => {
    if (!timeString) return 'Never'
    const date = new Date(timeString)
    const now = new Date()
    const diffMs = now - date
    const diffMins = Math.floor(diffMs / (1000 * 60))
    
    if (diffMins < 1) return 'Just now'
    if (diffMins < 60) return `${diffMins}m ago`
    
    const diffHours = Math.floor(diffMins / 60)
    if (diffHours < 24) return `${diffHours}h ago`
    
    const diffDays = Math.floor(diffHours / 24)
    return `${diffDays}d ago`
  }

  const sortedSources = [...sources].sort((a, b) => {
    // Sort by status priority: failed > unstable > running > healthy
    const getStatusPriority = (source) => {
      if (source.consecutive_failures >= 3) return 4
      if (source.consecutive_failures > 0) return 3
      if (source.is_executing) return 2
      return 1
    }
    
    const priorityDiff = getStatusPriority(b) - getStatusPriority(a)
    if (priorityDiff !== 0) return priorityDiff
    
    // Then by name
    return a.name.localeCompare(b.name)
  })

  return html`
    <div class="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700">
      <div class="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
        <div class="flex items-center justify-between">
          <h2 class="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <svg class="w-5 h-5 text-purple-500" fill="currentColor" viewBox="0 0 20 20">
              <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z"/>
            </svg>
            Sources Monitoring
          </h2>
          <span class="text-sm text-gray-600 dark:text-gray-400">
            ${sources.length} sources
          </span>
        </div>
      </div>
      
      <div class="p-6">
        <!-- Summary Stats -->
        <div class="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div class="text-center">
            <p class="text-xl font-bold text-green-600 dark:text-green-400">
              ${sources.filter(s => s.consecutive_failures === 0 && !s.is_executing).length}
            </p>
            <p class="text-xs text-gray-600 dark:text-gray-400">Healthy</p>
          </div>
          
          <div class="text-center">
            <p class="text-xl font-bold text-blue-600 dark:text-blue-400">
              ${sources.filter(s => s.is_executing).length}
            </p>
            <p class="text-xs text-gray-600 dark:text-gray-400">Running</p>
          </div>
          
          <div class="text-center">
            <p class="text-xl font-bold text-yellow-600 dark:text-yellow-400">
              ${sources.filter(s => s.consecutive_failures > 0 && s.consecutive_failures < 3).length}
            </p>
            <p class="text-xs text-gray-600 dark:text-gray-400">Unstable</p>
          </div>
          
          <div class="text-center">
            <p class="text-xl font-bold text-red-600 dark:text-red-400">
              ${sources.filter(s => s.consecutive_failures >= 3).length}
            </p>
            <p class="text-xs text-gray-600 dark:text-gray-400">Failed</p>
          </div>
        </div>

        <!-- Sources List -->
        <div class="space-y-3">
          ${sortedSources.map(source => html`
            <div key=${source.id} class="border border-gray-200 dark:border-gray-600 rounded-lg p-4">
              <div class="flex items-center justify-between">
                <div class="flex items-center gap-3 flex-1">
                  ${getSourceStatusIcon(source)}
                  <div class="flex-1">
                    <div class="flex items-center gap-2 mb-1">
                      <h3 class="font-medium text-gray-900 dark:text-white">
                        ${source.name}
                      </h3>
                      <span class="${getSourceStatusColor(source)} px-2 py-1 rounded-full text-xs font-medium border">
                        ${getSourceStatusText(source)}
                      </span>
                      <span class="${getPriorityColor(source.priority)} text-xs font-medium">
                        ${getPriorityText(source.priority)}
                      </span>
                      ${!source.is_active && html`
                        <span class="bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400 px-2 py-1 rounded-full text-xs">
                          Inactive
                        </span>
                      `}
                    </div>
                    <div class="flex items-center gap-4 text-xs text-gray-600 dark:text-gray-400">
                      <span>Frequency: ${source.frequency_minutes}m</span>
                      <span>Timeout: ${source.timeout_seconds}s</span>
                      <span>Articles: ${source.articles_last_24h || 0}</span>
                      ${source.consecutive_failures > 0 && html`
                        <span class="text-red-600 dark:text-red-400">
                          Failures: ${source.consecutive_failures}
                        </span>
                      `}
                    </div>
                  </div>
                </div>
                
                <div class="text-right text-xs text-gray-500 dark:text-gray-400">
                  <div>Last scraped: ${formatTime(source.last_scraped)}</div>
                  ${source.last_success && html`
                    <div>Last success: ${formatTime(source.last_success)}</div>
                  `}
                  ${source.last_error && html`
                    <div class="text-red-600 dark:text-red-400 max-w-xs truncate" title=${source.last_error}>
                      Error: ${source.last_error}
                    </div>
                  `}
                </div>
              </div>
            </div>
          `)}
        </div>

        ${sources.length === 0 && html`
          <div class="text-center py-8">
            <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"/>
            </svg>
            <h3 class="mt-2 text-sm font-medium text-gray-900 dark:text-white">No Sources Found</h3>
            <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
              No news sources are currently configured.
            </p>
          </div>
        `}
      </div>
    </div>
  `
}